import { getOriginalPeaksApi } from "api/fastApi/originalPeaks";
import { computePeaks, Peaks } from "utils/audioPeaks";

/**
 * Откуда русская дорожка берёт пики для рисования.
 *
 * 4119e839, папка 21, 02.10.2026: «нет аудио дорожки на русском». Пики оригинала получались только
 * разбором mp3 в браузере (computePeaks). Папка 21 — 6,2 МБ / 389,6 с; медленный ноутбук не
 * успевал за 15 с, дорожка рисовалась плоской, а через минуту в Chrome у её холстов было 0
 * закрашенных точек. Теперь сначала спрашивается сервер редактора — он считает те же пики один
 * раз и отдаёт готовыми, — и только если его ответ не годится, mp3 разбирается в браузере ровно
 * так, как раньше: тем же computePeaks, в голове очереди.
 */

export const ORIGINAL_PEAKS_BUCKETS = 4096;

/**
 * Сколько ждать сервер. Срок рисования — 15 с (AudioTrackOriginal); после 8 с ожидания разбору в
 * браузере остаётся ещё 7, а опоздавшие пики дорисовываются на ближайшей паузе.
 */
export const SERVER_PEAKS_TIMEOUT_MS = 8000;

/**
 * Разница длительностей, которую ещё прощаем: сервер и браузер меряют mp3 по-разному (задержка
 * кодера, оценка длины). Больше — значит, сервер описал другой файл, чем тот, что играет.
 */
const durationTolerance = (ms: number) => Math.max(2000, ms * 0.01);

/**
 * Пики сервера, если ими можно рисовать, иначе null.
 *
 * Годится только ровно 2 × buckets конечных чисел — тот же вид, что даёт bucketPeaks. Если уже
 * известна длительность играющего файла (`playedDurationMs` > 0), а сервер назвал другую — это
 * пики другого файла (например, выросшего mp3 эфира): нарисованная волна разъехалась бы со звуком.
 */
export const usableServerPeaks = (answer: unknown, buckets: number, playedDurationMs = 0): Peaks | null => {
    if (!answer || typeof answer !== "object") return null;
    const { peaks, buckets: answered, duration_ms: durationMs } = answer as Record<string, unknown>;
    if (!Array.isArray(peaks) || peaks.length !== 2 * buckets) return null;
    if (answered !== undefined && Number(answered) !== buckets) return null;
    if (!peaks.every((value) => typeof value === "number" && Number.isFinite(value))) return null;
    if (
        playedDurationMs > 0 &&
        typeof durationMs === "number" &&
        durationMs > 0 &&
        Math.abs(durationMs - playedDurationMs) > durationTolerance(playedDurationMs)
    ) {
        return null;
    }
    return peaks as Peaks;
};

export interface LoadOriginalPeaksArgs {
    /** Что играет дорожка (blob mp3 папки) — его и разбирает браузер, если сервер не помог. */
    mp3Url: string;
    mediaId?: string | null;
    folder?: number | null;
    /**
     * Открыт живой эфир. Его mp3 растёт, и сервер нарисовал бы файл длиннее того, что скачан, —
     * эфир, как и раньше, разбирается в браузере.
     */
    liveStream?: boolean;
    signal: AbortSignal;
    buckets?: number;
    serverTimeoutMs?: number;
    /** Длительность играющего файла в мс, 0 — ещё не известна. Читается в момент ответа. */
    playedDurationMs?: () => number;
    askServer?: typeof getOriginalPeaksApi;
    decode?: typeof computePeaks;
}

export type OriginalPeaksSource = "server" | "decode";

const askServerForPeaks = async ({
    mediaId,
    folder,
    liveStream,
    signal,
    buckets,
    timeoutMs,
    playedDurationMs,
    askServer,
}: {
    mediaId?: string | null;
    folder?: number | null;
    liveStream?: boolean;
    signal: AbortSignal;
    buckets: number;
    timeoutMs: number;
    playedDurationMs?: () => number;
    askServer: typeof getOriginalPeaksApi;
}): Promise<Peaks | null> => {
    if (liveStream || !mediaId || typeof folder !== "number" || !Number.isFinite(folder) || folder <= 0) {
        return null;
    }
    if (signal.aborted) return null;
    const request = new AbortController();
    const abort = () => request.abort();
    signal.addEventListener("abort", abort);
    let timer: number | undefined;
    // Не только abort: срок должен сработать и тогда, когда запрос отмену не слышит.
    const silence = new Promise<null>((resolve) => {
        timer = window.setTimeout(() => {
            request.abort();
            resolve(null);
        }, timeoutMs);
    });
    try {
        const answer = await Promise.race([
            askServer({ mediaId, folder, buckets, signal: request.signal }),
            silence,
        ]);
        return usableServerPeaks(answer, buckets, playedDurationMs ? playedDurationMs() : 0);
    } catch {
        // 404 (нет mp3 или старый сервер), сеть, отмена — разбор в браузере решит сам.
        return null;
    } finally {
        window.clearTimeout(timer);
        signal.removeEventListener("abort", abort);
    }
};

/**
 * Пики оригинала: сервер, а если его ответ не годится — разбор mp3 в браузере.
 *
 * Разбор зовётся с priority=true, как и раньше: дорожка оригинала идёт в очереди разборов впереди
 * всех озвучек (utils/audioPeaks). Отменённый вызывающим (сменилась папка) — ничего не разбирает.
 */
export const loadOriginalPeaks = async ({
    mp3Url,
    mediaId,
    folder,
    liveStream,
    signal,
    buckets = ORIGINAL_PEAKS_BUCKETS,
    serverTimeoutMs = SERVER_PEAKS_TIMEOUT_MS,
    playedDurationMs,
    askServer = getOriginalPeaksApi,
    decode = computePeaks,
}: LoadOriginalPeaksArgs): Promise<{ peaks: Peaks; source: OriginalPeaksSource }> => {
    const fromServer = await askServerForPeaks({
        mediaId,
        folder,
        liveStream,
        signal,
        buckets,
        timeoutMs: serverTimeoutMs,
        playedDurationMs,
        askServer,
    });
    if (fromServer) return { peaks: fromServer, source: "server" };
    if (signal.aborted) return { peaks: [], source: "decode" };
    return { peaks: await decode(mp3Url, buckets, signal, true), source: "decode" };
};

/**
 * Вернуть новый элемент дорожки туда, где стоял старый. Возвращает «отцепиться».
 *
 * Новые пики react-waves отдаёт только перезагрузкой: строит НОВЫЙ <audio>, старый выбрасывает,
 * и новый начинает с нуля (однажды так playhead улетел с 391,75 с на 0,22 с). Время пишется сразу —
 * браузер, у которого ещё нет метаданных, запомнит его как позицию старта, — и ещё раз, когда
 * метаданные пришли, для браузера, который запись до них не принял.
 *
 * Если к тому моменту элемент передвинул кто-то другой (щелчок по волне, переход к реплике), его
 * слово последнее: восстанавливать поверх — значит отменить действие человека.
 *
 * announceUserSeek здесь не зовётся: это не жест человека, а возврат на то же место, и
 * расписание озвучки не должно принимать его за щелчок.
 */
export const restorePlayhead = (media: HTMLMediaElement, seconds: number): (() => void) => {
    if (!Number.isFinite(seconds) || seconds < 0) return () => undefined;
    const put = () => {
        try {
            media.currentTime = seconds;
        } catch {
            /* ещё нельзя — повторим на loadedmetadata */
        }
    };
    const near = (a: number, b: number) => Math.abs(a - b) < 0.05;
    put();
    if (media.readyState >= 1) return () => undefined;
    const answeredAfterPut = media.currentTime;
    const onMetadata = () => {
        detach();
        const now = media.currentTime;
        if (near(now, seconds)) return;
        if (!near(now, answeredAfterPut)) return; // передвинул кто-то другой
        put();
    };
    const detach = () => media.removeEventListener("loadedmetadata", onMetadata);
    media.addEventListener("loadedmetadata", onMetadata);
    return detach;
};
